import { SearchspringCollectionProvider } from 'libs/searchspring/fetchers/collection'

import clsx from 'clsx'

import { useEffect, useRef, useState } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { useScrollCache } from 'libs/searchspring/hooks/useScrollCache'
import { useFilterScroll } from 'libs/searchspring/hooks/useFilterScroll'
import { useScrollPositionKeeper } from 'libs/searchspring/hooks/useScrollPositionKeeper'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { useMerchedResults } from 'libs/searchspring/hooks/useMerchedResults'
import { FiltersProvider } from 'libs/searchspring/hooks/useFilters'
import { CustomFilter } from 'libs/searchspring/components/collection/filters/CustomFilter'
import { CollectionToolbar } from 'libs/searchspring/components/collection/CollectionToolbar'
import { FilterDrawer } from 'libs/searchspring/components/collection/filters/FilterDrawer'
import { Pagination } from 'libs/searchspring/components/collection/Pagination'
import { ResultItem } from 'libs/searchspring/components/ResultCardWrapper'
import { SkeletonCard } from 'libs/searchspring/components/SkeletonCard'

function CollectionGrid({ customFilter }) {
  history.scrollRestoration = 'manual'
  const [firstLoad, setFirstLoad] = useState(true)
  const [showSkeleton, setShowSkeleton] = useState(true)
  const hasStartedLoading = useRef(false)
  const { results, isLoading } = useSearchspringContext()
  const currentResults = useMerchedResults()
  const gridChange = results?.pagination?.currentPage
  const { setEntry } = useScrollPositionKeeper(gridChange)
  const { setScrollElement } = useFilterScroll()

  useEffect(() => {
    if (isLoading) hasStartedLoading.current = true
    if (!isLoading && hasStartedLoading.current) setFirstLoad(false)
  }, [isLoading])

  useEffect(() => {
    if (!isLoading && currentResults?.length > 0) {
      const timer = setTimeout(() => {
        setShowSkeleton(false)
      }, 150)

      return () => clearTimeout(timer)
    } else if (isLoading || firstLoad) {
      setShowSkeleton(true)
    }
  }, [isLoading, currentResults?.length, firstLoad])

  useScrollCache([currentResults, firstLoad])

  return (
    <div className="flex grow flex-col">
      <FiltersProvider>
        {customFilter && <CustomFilter filter={customFilter} />}
        <CollectionToolbar />
        <div className="flex grow flex-col md:grid md:grid-cols-min-full">
          <FilterDrawer />
          <div className="flex grow flex-col">
            <div
              ref={(el) => {
                setScrollElement(el)
                setEntry(el)
              }}
              className={clsx(
                'grid w-full grid-cols-2 gap-x-gutter gap-y-2 pb-3.5 [grid-auto-flow:row_dense] md:grid-cols-3 lg:grid-cols-4 xxxl:grid-cols-5 image-view-large:grid-cols-1 image-view-large:md:grid-cols-2 image-view-large:lg:grid-cols-3 image-view-large:xxxl:grid-cols-4',
                isLoading && firstLoad && 'min-h-screen-height'
              )}
            >
              {showSkeleton && firstLoad
                ? Array.from({ length: window.settings?.per_page || 12 }, (_, index) => (
                    <SkeletonCard key={`skeleton-${index}`} className="w-full" />
                ))
                : currentResults?.map((item, index) => (
                    <ResultItem
                      item={item}
                      key={item.handle ?? `item-${index}`}
                      className="wk-wishlist-button-target"
                      dataIndex={index.toString()}
                    />
                ))}
            </div>
            {currentResults.length > 0 && !firstLoad && <Pagination />}
          </div>
        </div>
      </FiltersProvider>
    </div>
  )
}

export function WrappedCollectionGrid({ customFilter, perPage }) {
  const resultsPerPage = perPage || window.settings?.per_page
  scrollHistoryManager.updateHistoryState()
  return (
    <SearchspringCollectionProvider handle={window.collection?.handle} resultsPerPage={resultsPerPage}>
      <CollectionGrid customFilter={customFilter} />
    </SearchspringCollectionProvider>
  )
}
